import React from 'react';
import PropTypes from 'prop-types';
import ApartmentIcon from '@fabric-ds/icons/dist/32/building.svg';
import HikingIcon from '@fabric-ds/icons/dist/32/hiking.svg';
import MountainIcon from '@fabric-ds/icons/dist/32/mountain.svg';
import FloorsIcon from '@fabric-ds/icons/dist/32/stairs.svg';
import BedIcon from '@fabric-ds/icons/dist/32/bed.svg';
import KvmIcon from '@fabric-ds/icons/dist/32/measure.svg';
import EnergyIcon from '@fabric-ds/icons/dist/32/energy.svg';
import DocumentIcon from '@fabric-ds/icons/dist/32/file.svg';
import PriceTagIcon from '@fabric-ds/icons/dist/32/tag.svg';
import { formattedPropertyTypes, toMinMaxString, displayEnergyLabel } from 'common/lib/helpers/TextUtils';
import * as _ from 'lodash';
import { useDeviceSize } from 'common/lib/hooks/useDeviceSize';
import {
    AD_TYPE_ETS,
    AD_TYPE_KFS,
    ALL_AD_STATUS_STATES,
    AD_TYPE_PTS,
    AD_TYPE_PTSF,
    AD_TYPE_PTS_UNIT,
    AD_TYPE_PTSF_UNIT,
} from 'common/lib/helpers/constants';
import { formatPriceLabelOrElse } from 'common/lib/helpers/formatPrice';
import * as S from './styles';
import { getUnits } from '../unitSelector/unitFilter';
import CollapsableArea from '../collapsableArea';
import CadastralInfo from '../cadastralInfo';

const iconSize = 32;

const KeyInfo = ({ data }) => {
    const {
        adType,
        propertyTypes,
        propertyType,
        numberOfFloors,
        energyColorCode,
        energyLabel,
        ownership,
        minBedRooms,
        maxBedRooms,
        bedrooms,
        floor,
        primaryRoomArea,
        usableArea,
        master,
        rooms,
        sharedCostIncludes,
        apartmentNumber,
        floorArea,
        outdoorAreaSize,
        sharedCostHedge,
        situation,
        plotArea,
        isPlotOwned,
        metersAboveSeaLevel,
        leaseholdFee,
        municipalityNumber,
        sectionNumber,
        propertyUnitNumber,
        cadastralUnitNumber,
        leaseholdUnitNumber,
        areaDescription,
        sunConditions,
    } = data;

    const { isMobile } = useDeviceSize();

    const feature = (name, value, IconType, unit) => {
        return (
            <S.Feature className="mt-16">
                <IconType width={iconSize} height={iconSize} style={{ color: '#767676' }} />
                <span className="ml-8">
                    <p className="mb-0">{name}</p>
                    <p className="font-bold mb-0">
                        {value} {unit && ` ${unit}`}
                    </p>
                </span>
            </S.Feature>
        );
    };

    const kfsFeatures = (
        <>
            {situation && feature('Beliggenhet', situation, HikingIcon)}
            {minBedRooms != null &&
                maxBedRooms != null &&
                feature(
                    'Soverom',
                    minBedRooms === maxBedRooms ? minBedRooms : `${minBedRooms} - ${maxBedRooms}`,
                    BedIcon
                )}
            {feature('Pris', 'Pris kommer', PriceTagIcon)}
            {feature('Boligtype', formattedPropertyTypes(propertyTypes), ApartmentIcon)}
            {feature('Eieform', ownership, DocumentIcon)}
        </>
    );

    const ptsFeatures = () => {
        const { units } = getUnits();

        const ptsBedrooms = toMinMaxString(
            units.map(unit => {
                return unit.bedrooms;
            })
        );

        const ptsPrimaryRoomArea = toMinMaxString(
            units.map(unit => {
                return unit.primaryRoomArea;
            })
        );

        const ptsUsableArea = toMinMaxString(
            units.map(unit => {
                return unit.usableArea;
            })
        );

        /*
            pts field propertyTypes is an aggregate of the pts-unit propertytype that is updated
            when the ad is persisted. For business center ads it is enough to check the pts field propertyTypes.
            for import ads we also need to check if there are any units, because the pts is created with a
            dummy propertyType (Enebolig) because of an AIS requirement. Otherwise an import pts ad without units
            will show up as Enebolig
        */
        const showPropertyType = !_.isEmpty(propertyTypes) && !_.isEmpty(units);

        return (
            <>
                {situation && feature('Beliggenhet', situation, HikingIcon)}
                {showPropertyType && feature('Boligtype', formattedPropertyTypes(propertyTypes), ApartmentIcon)}
                {(ptsBedrooms || ptsBedrooms === 0) && feature('Soverom', ptsBedrooms, BedIcon)}
                {Number.isFinite(numberOfFloors) && feature('Antall etasjer', numberOfFloors, FloorsIcon)}
                {Number.isFinite(ptsPrimaryRoomArea) && ptsPrimaryRoomArea !== 0
                    ? feature('Primærrom', ptsPrimaryRoomArea, KvmIcon, 'm²')
                    : ''}
                {Number.isFinite(ptsUsableArea) && ptsUsableArea !== 0
                    ? feature('Bruksareal', ptsUsableArea, KvmIcon, 'm²')
                    : ''}
                {metersAboveSeaLevel && feature('Meter over havet', metersAboveSeaLevel, MountainIcon, 'moh.')}
                {ownership && feature('Eieform', ownership, DocumentIcon)}
                {(energyColorCode || energyLabel) &&
                    feature(
                        'Energimerking',
                        energyColorCode && energyLabel
                            ? `${energyLabel} - ${energyColorCode}`
                            : ` ${energyColorCode || energyLabel}`,
                        EnergyIcon
                    )}
            </>
        );
    };

    const ptsUnitFeatures = (
        <>
            {master && master.situation && feature('Beliggenhet', master.situation, HikingIcon)}
            {propertyType && feature('Boligtype', propertyType, ApartmentIcon)}
            {Number.isFinite(floor) && feature('Etasje', floor, FloorsIcon)}
            {(bedrooms || bedrooms === 0) && feature('Soverom', bedrooms, BedIcon)}
            {Number.isFinite(primaryRoomArea) && primaryRoomArea !== 0
                ? feature('Primærrom', primaryRoomArea, KvmIcon, 'm²')
                : ''}
            {Number.isFinite(usableArea) && usableArea !== 0 ? feature('Bruksareal', usableArea, KvmIcon, 'm²') : ''}
            {Number.isFinite(plotArea) && plotArea !== 0
                ? feature(`Tomt (${isPlotOwned ? 'eiet' : 'festet'})`, plotArea, KvmIcon, 'm²')
                : ''}
        </>
    );

    const etsFeatures = (
        <>
            {propertyType && feature('Boligtype', propertyType, ApartmentIcon)}
            {(bedrooms || bedrooms === 0) && feature('Soverom', bedrooms, BedIcon)}
            {Number.isFinite(numberOfFloors) && feature('Antall etasjer', numberOfFloors, FloorsIcon)}
            {Number.isFinite(primaryRoomArea) && primaryRoomArea !== 0
                ? feature('Primærrom', primaryRoomArea, KvmIcon, 'm²')
                : ''}
            {Number.isFinite(usableArea) && usableArea !== 0 ? feature('Bruksareal', usableArea, KvmIcon, 'm²') : ''}
            {(energyColorCode || energyLabel) &&
                feature(
                    'Energimerking',
                    energyColorCode && energyLabel
                        ? `${energyLabel} - ${energyColorCode}`
                        : ` ${energyColorCode || energyLabel}`,
                    EnergyIcon
                )}
            {ownership && feature('Eieform', ownership, DocumentIcon)}
        </>
    );

    const getFeaturesByAdType = () => {
        switch (adType) {
            case AD_TYPE_KFS:
                return kfsFeatures;
            case AD_TYPE_PTS:
            case AD_TYPE_PTSF:
                return ptsFeatures();
            case AD_TYPE_ETS:
                return etsFeatures;
            case AD_TYPE_PTS_UNIT:
            case AD_TYPE_PTSF_UNIT:
                return ptsUnitFeatures;
            default:
                return null;
        }
    };

    const detail = (name, value, unit, fullWidth, description) => {
        if (value || value === 0) {
            return (
                <S.Detail
                    mobile={isMobile}
                    columns={adType === AD_TYPE_ETS ? 2 : 3}
                    className={`mt-16 ${fullWidth ? 'fullwidth-detail' : ''}`}
                >
                    <p className={description ? 'font-bold' : ''}>{name}</p>
                    <p className={description ? '' : 'font-bold'}>
                        {value}
                        {unit && ` ${unit}`}
                    </p>
                </S.Detail>
            );
        }
        return null;
    };

    const ptsUnitDetails = (
        <>
            {master && master.ownership && detail('Eieform', master.ownership)}
            {detail('Rom', rooms)}
            {master && detail('Energimerking', displayEnergyLabel(master.energyLabel, master.energyColorCode))}
            {adType === AD_TYPE_PTSF_UNIT && detail('Festeavgift', formatPriceLabelOrElse(leaseholdFee, null))}
            {sharedCostIncludes && detail('Husleie/felleskostnader inkluderer', sharedCostIncludes, null, true, true)}
        </>
    );

    const etsDetails = (
        <>
            {detail('Bolignummer', apartmentNumber)}
            {detail('Rom', rooms)}
            {detail('Etasje', floor)}
            {detail('Utendørsareal', outdoorAreaSize, 'm²')}
            {detail('Grunnflate', floorArea, 'm²')}
            {sharedCostHedge !== null && detail('Sikringsordning', sharedCostHedge ? 'Ja' : 'Nei')}
            {sharedCostIncludes && detail('Husleie/felleskostnader inkluderer', sharedCostIncludes, null, true, true)}
            {areaDescription && detail('Arealbeskrivelse', areaDescription, null, true, true)}
            {sunConditions && detail('Solforhold', sunConditions, null, true, true)}
        </>
    );

    const getDetailsByAdType = () => {
        switch (adType) {
            case AD_TYPE_ETS:
                return etsDetails;
            case AD_TYPE_PTS_UNIT:
            case AD_TYPE_PTSF_UNIT:
                return ptsUnitDetails;
            default:
                return null;
        }
    };

    return (
        <div className="border-b-2 border-bluegray-200 py-32">
            <h3>Nøkkelinfo</h3>
            {getDetailsByAdType() ? (
                <CollapsableArea showMoreText="Se flere detaljer" showLessText="Se færre detaljer" className="mt-32">
                    <S.Grid isMobile={isMobile}>{getFeaturesByAdType()}</S.Grid>
                    <S.Wrapper className="mt-32">{getDetailsByAdType()}</S.Wrapper>
                    <CadastralInfo
                        municipalityNumber={municipalityNumber}
                        sectionNumber={sectionNumber}
                        propertyUnitNumber={propertyUnitNumber}
                        cadastralUnitNumber={cadastralUnitNumber}
                        leaseholdUnitNumber={leaseholdUnitNumber}
                    />
                </CollapsableArea>
            ) : (
                <S.Grid isMobile={isMobile}>{getFeaturesByAdType()}</S.Grid>
            )}
        </div>
    );
};

KeyInfo.propTypes = {
    data: PropTypes.shape({
        propertyType: PropTypes.string,
        adType: PropTypes.string,
        units: PropTypes.array,
        energyColorCode: PropTypes.string,
        energyLabel: PropTypes.string,
        propertyTypes: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
        numberOfFloors: PropTypes.number,
        ownership: PropTypes.string,
        situation: PropTypes.string,
        minBedRooms: PropTypes.number,
        maxBedRooms: PropTypes.number,
        bedrooms: PropTypes.number,
        floor: PropTypes.number,
        primaryRoomArea: PropTypes.number,
        usableArea: PropTypes.number,
        adStatus: PropTypes.oneOf(ALL_AD_STATUS_STATES),
        totalPrice: PropTypes.number,
        salesCostSum: PropTypes.number,
        rooms: PropTypes.number,
        apartmentNumber: PropTypes.string,
        sharedCostAmountAfterDeductionFreePeriod: PropTypes.number,
        sharedCostIncludes: PropTypes.string,
        floorArea: PropTypes.number,
        plotArea: PropTypes.number,
        outdoorAreaSize: PropTypes.number,
        leaseholdFee: PropTypes.number,
        sharedCostHedge: PropTypes.bool,
        isPlotOwned: PropTypes.bool,
        metersAboveSeaLevel: PropTypes.number,
        municipalityNumber: PropTypes.number,
        sectionNumber: PropTypes.number,
        propertyUnitNumber: PropTypes.number,
        cadastralUnitNumber: PropTypes.number,
        leaseholdUnitNumber: PropTypes.number,
        areaDescription: PropTypes.string,
        sunConditions: PropTypes.string,
        master: PropTypes.shape({
            ownership: PropTypes.string,
            situation: PropTypes.string,
            energyLabel: PropTypes.string,
            energyColorCode: PropTypes.string,
        }),
    }).isRequired,
};

export default KeyInfo;
